/**
 * WF-Back-Btn - Composable Back button for wizard forms
 *
 * Dispatches 'wf:nav-back' event that bubbles to wizard-form.
 * Hidden on the first step by default.
 *
 * @example
 * <wf-step data-step="2">
 *   <wf-layout direction="row" justify="between">
 *     <wf-back-btn></wf-back-btn>
 *     <wf-next-btn></wf-next-btn>
 *   </wf-layout>
 * </wf-step>
 */

import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { NavigationButtonBase } from './base/navigation-button-base.js';
import { wfBackBtnStyles } from './wf-back-btn.styles.js';
import './wf-badge.js';

@customElement('wf-back-btn')
export class WfBackBtn extends NavigationButtonBase {
  static styles = wfBackBtnStyles;

  // ============================================
  // Properties
  // ============================================

  /**
   * Custom label (default: "Back")
   */
  @property({ type: String })
  label = 'Back';

  /**
   * Show "Esc" badge (default: true)
   */
  @property({ type: Boolean, attribute: 'show-shortcut' })
  showShortcut = true;

  /**
   * Disabled state
   */
  @property({ type: Boolean })
  disabled = false;

  /**
   * Hide on first step (default: true)
   */
  @property({ type: Boolean, attribute: 'hide-on-first' })
  hideOnFirst = true;

  // ============================================
  // Abstract Implementation
  // ============================================

  protected _getEventName(): string {
    return 'wf:nav-back';
  }

  // ============================================
  // Event Handlers
  // ============================================

  private _handleClick = (): void => {
    if (this.disabled) {return;}
    this._dispatchNavEvent();
  };

  // ============================================
  // Render
  // ============================================

  override render() {
    if (this.hideOnFirst && this._isFirstStep) {
      return nothing;
    }

    return html`
      <button
        type="button"
        class="wf-btn wf-btn-back"
        ?disabled="${this.disabled}"
        @click="${this._handleClick}"
        data-testid="wf-back-btn"
      >
        ${this.showShortcut
          ? html`
              <span class="wf-btn-shortcut">
                <wf-badge variant="button-secondary">Esc</wf-badge>
              </span>
            `
          : nothing}
        ${this.label}
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wf-back-btn': WfBackBtn;
  }
}
